import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { ArrowsFullscreen, CheckCircleFill, ExclamationTriangleFill, XCircleFill } from "react-bootstrap-icons";
import { getDeviceMqttTopics } from "../commonApi";
import { Loader } from "../Loader";
import { MqttWorkspace } from "./MqttTestClient";

// MQTT Topics tab of the device health page: the device's topics (GET /things/{thingName}/mqtt-topics) next to
// a live MQTT test client, so each topic can be subscribed to or published right here. Topics the hub uses are
// checked against its IoT policies; only problems are shown.

const PROBLEM = new Set(["NOT_ALLOWED", "DENIED", "UNKNOWN"]);

export const testClientUrl = (thingName, { sub = [], pub } = {}) => {
  const qs = new URLSearchParams();
  if (thingName) qs.set("thing", thingName);
  sub.forEach((s) => qs.append("sub", s));
  if (pub) qs.set("pub", pub);
  return `/devices/mqtt-test-client?${qs.toString()}`;
};

function MqttTopicsTab({ thingName, reloadKey }) {
  const navigate = useNavigate();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    setError(null);
    getDeviceMqttTopics(thingName)
      .then((res) => !cancelled && setData(res.data?.data || null))
      .catch((e) => {
        if (cancelled) return;
        setData(null);
        setError(
          e.response?.status === 403
            ? "You need the ADMIN role to see device MQTT topics"
            : e.response?.data?.error?.message || e.message
        );
      })
      .finally(() => !cancelled && setLoading(false));
    return () => {
      cancelled = true;
    };
  }, [thingName, reloadKey]);

  if (loading) {
    return (
      <div className="spin-loader">
        <Loader />
      </div>
    );
  }
  if (error) return <div className="dhc-alert dhc-alert-fail">{error}</div>;
  if (!data) return <div className="dhc-empty">No MQTT topic data</div>;

  const topics = data.topics || [];
  const problems = topics.filter((t) => PROBLEM.has(t.policyStatus));
  const checked = topics.filter((t) => t.policyStatus && t.policyStatus !== "NOT_CHECKED").length;

  return (
    <>
      <div className="dhc-section-head">
        <div>
          <h3>MQTT topics</h3>
          <p className="dhc-muted">
            Subscribe to or publish any topic of this {data.child ? "device and its hub" : "device"}
            {data.child && (
              <>
                {" "}
                · MQTT connection held by hub <span className="dhc-mono">{data.hubThingName}</span>
              </>
            )}
            . Messages you publish reach the real device and Lambdas.
          </p>
        </div>
        <button type="button" className="dhc-btn dhc-btn-outline dhc-btn-sm" onClick={() => navigate(testClientUrl(thingName))}>
          <ArrowsFullscreen size={12} /> Full page
        </button>
      </div>

      {data.policyError ? (
        <div className="dhc-alert dhc-alert-warn">
          <ExclamationTriangleFill style={{ marginTop: 3, flexShrink: 0 }} />
          <span>{data.policyError}</span>
        </div>
      ) : problems.length ? (
        <div className="dhc-alert dhc-alert-fail">
          <XCircleFill style={{ marginTop: 3, flexShrink: 0 }} />
          <div style={{ minWidth: 0 }}>
            <b>
              {problems.length} topic{problems.length > 1 ? "s" : ""} not allowed by the certificate's policies (
              {data.policies.join(", ") || "none"})
            </b>
            {problems.map((t) => (
              <div key={t.topic} className="dhc-mono mq-break" style={{ marginTop: 2 }}>
                {t.topic} — {t.permissions.filter((p) => PROBLEM.has(p.status)).map((p) => p.action).join(", ")}
              </div>
            ))}
          </div>
        </div>
      ) : (
        checked > 0 && (
          <div className="dhc-policy-ok">
            <CheckCircleFill /> All {checked} topics the hub uses are allowed by {data.policies.join(", ")}
          </div>
        )
      )}

      <MqttWorkspace thing={thingName} topicsData={data} />
    </>
  );
}

export default MqttTopicsTab;
